import mapboxgl from 'mapbox-gl';

export const MAP_STYLES = {
  SATELLITE: {
    url: 'mapbox://styles/buckrivard/clertmasg000t01mvw0cvb340',
    name: 'Satellite',
  },
  STREET: { url: 'mapbox://styles/buckrivard/clerial3b000m01mv5fntb5r1', name: 'Street' },
};

export const MAP_PIN_STATUS_FILTERS = {
  ACTIVE: 'active',
  SOLD: 'sold',
  PENDING: 'pending',
  RENTED: 'rented',
  'SELECTED COMPS': 'selected comps',
  OTHER: 'other',
} as const;

export type PinStatusFilter = (typeof MAP_PIN_STATUS_FILTERS)[keyof typeof MAP_PIN_STATUS_FILTERS];

export const DEFAULT_SELECTED_STATUSES: PinStatusFilter[] = [
  MAP_PIN_STATUS_FILTERS['SELECTED COMPS'],
  MAP_PIN_STATUS_FILTERS.ACTIVE,
  MAP_PIN_STATUS_FILTERS.SOLD,
  MAP_PIN_STATUS_FILTERS.PENDING,
  MAP_PIN_STATUS_FILTERS.RENTED,
];

export const INITIAL_STYLE = MAP_STYLES.SATELLITE;

export const MAP_SOURCES = Object.freeze({
  SUBJECT: 'subject',
  PINS: 'pins',
});

export const MAP_LAYERS = Object.freeze({
  SUBJECT: 'subject',
  PINS_HOVER: 'pins-hover',
  PINS: 'pins',
  PINS_HIT: 'pins-hit',
});

export const MAP_LAYER_DEFINITIONS: {
  SUBJECT: mapboxgl.AnyLayer;
  PINS_HOVER: mapboxgl.AnyLayer;
  PINS: mapboxgl.AnyLayer;
  PINS_HIT: mapboxgl.AnyLayer;
} = Object.freeze({
  PINS_HOVER: {
    id: MAP_LAYERS.PINS_HOVER,
    type: 'circle',
    source: MAP_SOURCES.PINS,
    filter: ['boolean', ['get', 'hovered'], false],
    paint: {
      'circle-color': '#DAE1FE',
      'circle-radius': 14,
      'circle-opacity': 1,
      'circle-stroke-color': '#CDD3D7',
      'circle-stroke-width': 1,
    },
  },
  PINS: {
    id: MAP_LAYERS.PINS,
    type: 'symbol',
    source: MAP_SOURCES.PINS,
    layout: {
      'icon-image': ['get', 'icon'],
      'icon-anchor': 'center',
      'icon-allow-overlap': true,
      'icon-size': [
        'case',
        ['boolean', ['get', 'hovered'], false],
        1.08,
        ['boolean', ['get', 'selected'], false],
        1.04,
        1,
      ],
      'symbol-sort-key': [
        '+',
        ['case', ['boolean', ['get', 'active'], false], 3, 0],
        ['case', ['boolean', ['get', 'selected'], false], 2, 0],
        ['case', ['boolean', ['get', 'hovered'], false], 1, 0],
      ],
      'text-field': ['get', 'label'],
      'text-anchor': 'center',
      'text-offset': [0, -0.5],
      'text-size': [
        'case',
        ['boolean', ['get', 'hovered'], false],
        15,
        ['boolean', ['get', 'selected'], false],
        14.5,
        14,
      ],
      'text-allow-overlap': true,
    },
    paint: {
      'text-color': ['case', ['boolean', ['get', 'active'], false], '#fff', '#072138'],
    },
  },
  PINS_HIT: {
    id: MAP_LAYERS.PINS_HIT,
    type: 'circle',
    source: MAP_SOURCES.PINS,
    paint: {
      'circle-radius': 18,
      'circle-opacity': 0,
    },
  },
  SUBJECT: {
    id: MAP_LAYERS.SUBJECT,
    type: 'symbol',
    source: MAP_SOURCES.SUBJECT,
    layout: {
      'icon-image': 'Subject',
      'icon-size': 1.5,
      'icon-allow-overlap': false,
    },
  },
});

export const DRAW_MAPBOX_STYLES = [
  {
    id: 'gl-draw-polygon-fill',
    type: 'fill',
    filter: ['all', ['==', '$type', 'Polygon']],
    paint: {
      'fill-color': ['case', ['==', ['get', 'active'], 'true'], 'orange', 'blue'],
      'fill-opacity': 0.1,
    },
  },
  {
    id: 'gl-draw-lines',
    type: 'line',
    filter: ['any', ['==', '$type', 'LineString'], ['==', '$type', 'Polygon']],
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': ['case', ['==', ['get', 'active'], 'true'], 'orange', 'blue'],
      'line-dasharray': [
        'case',
        ['==', ['get', 'active'], 'true'],
        ['literal', [0.2, 2]],
        ['literal', [0.2, 2]],
      ],
      'line-width': 2,
    },
  },
  {
    id: 'gl-draw-point-outer',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 7, 5],
      'circle-color': 'white',
    },
  },
  {
    id: 'gl-draw-point-inner',
    type: 'circle',
    filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'feature']],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 5, 3],
      'circle-color': ['case', ['==', ['get', 'active'], 'true'], 'orange', 'blue'],
    },
  },
  {
    id: 'gl-draw-vertex-outer',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['==', 'meta', 'vertex'],
      ['!=', 'mode', 'simple_select'],
    ],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 7, 5],
      'circle-color': 'white',
    },
  },
  {
    id: 'gl-draw-vertex-inner',
    type: 'circle',
    filter: [
      'all',
      ['==', '$type', 'Point'],
      ['==', 'meta', 'vertex'],
      ['!=', 'mode', 'simple_select'],
    ],
    paint: {
      'circle-radius': ['case', ['==', ['get', 'active'], 'true'], 5, 3],
      'circle-color': 'orange',
    },
  },
  {
    id: 'gl-draw-midpoint',
    type: 'circle',
    filter: ['all', ['==', 'meta', 'midpoint']],
    paint: { 'circle-radius': 3, 'circle-color': 'orange' },
  },
];
